import React, { useEffect, useState, useMemo, useCallback } from "react";
import { ShieldCheck, Search, Users, DollarSign, Calendar, Check } from "lucide-react";
import { fetchMemberships, setCustomerMembership } from "@/lib/dashboardApi";
import { toast } from "sonner";

const TIERS = [
  { id: "standard", label: "None" },
  { id: "essential", label: "Essential" },
  { id: "advantage", label: "Advantage" },
  { id: "elite", label: "Elite" },
];
const TIER_BADGE = {
  standard: "bg-slate-100 text-slate-600 border-slate-200",
  essential: "bg-sky-100 text-sky-700 border-sky-200",
  advantage: "bg-emerald-100 text-emerald-700 border-emerald-200",
  elite: "bg-amber-100 text-amber-700 border-amber-200",
};
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`membership-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-2 text-slate-500 text-sm font-medium"><Icon className="w-4 h-4" /> {label}</div>
    <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

const MembershipManager = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterTier, setFilterTier] = useState("all");
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchMemberships();
      setData(d);
      const init = {};
      (d.customers || []).forEach((c) => { init[c.key] = { tier: c.tier, renewal_date: c.renewal_date || "" }; });
      setDrafts(init);
    } catch { toast.error("Failed to load memberships"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const setDraft = (key, patch) => setDrafts((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
  const save = async (c) => {
    const draft = drafts[c.key] || {};
    setSavingKey(c.key);
    try {
      await setCustomerMembership(c.key, draft.tier || "standard", draft.renewal_date || "");
      toast.success(`Saved ${c.name || "customer"} → ${TIERS.find((t) => t.id === draft.tier)?.label || draft.tier}`);
      setData((prev) => {
        const prices = prev.prices || {};
        const customers = prev.customers.map((x) => x.key === c.key
          ? { ...x, tier: draft.tier, renewal_date: draft.renewal_date, monthly_price: prices[draft.tier]?.monthly || 0, annual_price: prices[draft.tier]?.annual || 0 }
          : x);
        const tier_counts = { standard: 0, essential: 0, advantage: 0, elite: 0 };
        let mrr = 0;
        customers.forEach((x) => { tier_counts[x.tier] = (tier_counts[x.tier] || 0) + 1; mrr += Number(prices[x.tier]?.monthly || 0); });
        const members = tier_counts.essential + tier_counts.advantage + tier_counts.elite;
        return { ...prev, customers, summary: { ...prev.summary, tier_counts, members, mrr: Math.round(mrr * 100) / 100, arr: Math.round(mrr * 12 * 100) / 100 } };
      });
    } catch { toast.error("Save failed"); }
    finally { setSavingKey(null); }
  };
  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.customers || [];
    if (filterTier !== "all") list = list.filter((c) => c.tier === filterTier);
    if (q.trim()) { const ql = q.trim().toLowerCase(); list = list.filter((c) => `${c.name} ${c.phone} ${c.email} ${c.address}`.toLowerCase().includes(ql)); }
    return list;
  }, [data, filterTier, q]);
  const s = data?.summary;
  return (
    <div className="space-y-6" data-testid="membership-manager">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-6 h-6 text-[#F97316]" />
        <div>
          <h2 className="text-xl font-bold text-slate-900">Membership Manager</h2>
          <p className="text-sm text-slate-500">Set every customer's Comfort Club tier &amp; renewal date in one place.</p>
        </div>
      </div>
      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Active Members" value={s.members} sub={`of ${s.total} customers`} />
          <StatCard icon={DollarSign} label="MRR" value={money(s.mrr)} sub="recurring / month" />
          <StatCard icon={DollarSign} label="ARR" value={money(s.arr)} sub="recurring / year" />
          <StatCard icon={ShieldCheck} label="Elite" value={s.tier_counts?.elite || 0} sub={`Advantage ${s.tier_counts?.advantage || 0} · Essential ${s.tier_counts?.essential || 0}`} />
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center border-b border-slate-100">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="membership-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email…" className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-[#F97316] outline-none" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setFilterTier("all")} data-testid="membership-filter-all" className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filterTier === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>All</button>
            {TIERS.map((t) => (
              <button key={t.id} onClick={() => setFilterTier(t.id)} data-testid={`membership-filter-${t.id}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filterTier === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>{t.label}</button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Loading customers…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">No customers match.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 font-semibold">Current</th>
                  <th className="px-4 py-3 font-semibold">Tier</th>
                  <th className="px-4 py-3 font-semibold">Renewal Date</th>
                  <th className="px-4 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c, i) => {
                  const draft = drafts[c.key] || { tier: c.tier, renewal_date: c.renewal_date };
                  const dirty = draft.tier !== c.tier || (draft.renewal_date || "") !== (c.renewal_date || "");
                  return (
                    <tr key={c.key} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`membership-row-${i}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-800">{c.name || "—"}</p>
                        <p className="text-xs text-slate-400">{c.phone || c.email || c.key}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${TIER_BADGE[c.tier] || TIER_BADGE.standard}`}>{TIERS.find((t) => t.id === c.tier)?.label || "None"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <select value={draft.tier} data-testid={`membership-tier-select-${i}`} onChange={(e) => setDraft(c.key, { tier: e.target.value })} className="px-2 py-1.5 rounded-lg border border-slate-300 text-sm focus:border-[#F97316] outline-none">
                          {TIERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="relative">
                          <Calendar className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                          <input type="date" value={draft.renewal_date || ""} data-testid={`membership-renewal-${i}`} onChange={(e) => setDraft(c.key, { renewal_date: e.target.value })} className="pl-8 pr-2 py-1.5 rounded-lg border border-slate-300 text-sm focus:border-[#F97316] outline-none" />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => save(c)} disabled={!dirty || savingKey === c.key} data-testid={`membership-save-${i}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${dirty ? "bg-[#F97316] text-white hover:bg-[#ea6a0c]" : "bg-slate-100 text-slate-400 cursor-default"}`}>
                          <Check className="w-3.5 h-3.5" /> {savingKey === c.key ? "Saving…" : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
export default MembershipManager;
